import { Component, OnInit } from '@angular/core';
import { AlertsService } from '../../services/alerts.service';
import { LoaderService } from '../../services/loader.service';
import { BaseService } from '../../shared/services/base.service';
import { DialogConfig } from '../dialog/dialog-config';
import { DialogModalSize, DialogRef } from '../dialog/dialog-ref';
import { BiometricAuthComponent } from '../biometric-auth/biometric-auth.component';

/**
 * The cycle ledger for one approved pre-authorization — every treatment day of a cyclic procedure,
 * and the biometric that stands behind it.
 *
 * A cyclic benefit (dialysis is the standard case) is approved once and delivered over weeks. Each
 * session needs its own authentication taken *before* the treatment, so this cannot live on the
 * pre-auth form: by the time the second session happens that form has been submitted and closed.
 * It opens instead from the authorization worklist, which is where the ward comes back to the case
 * day after day.
 *
 * What it does is deliberately narrow. It shows what has already been recorded, opens the existing
 * {@link BiometricAuthComponent} for the next outstanding cycle, and posts the verified txnId to
 * `RECORD_CYCLE_BIOMETRIC`. Every rule — the cycle's number, the one-per-calendar-day limit of
 * PAYR-1369, the approved ceiling — is applied by the backend against the stored pre-auth, never
 * computed here. The guards below are the same rules mirrored client-side so the operator sees a
 * refusal immediately instead of after a round trip; they are a courtesy, not the enforcement.
 */
@Component({
  selector: 'app-cyclic-treatment',
  templateUrl: './cyclic-treatment.component.html',
  styleUrls: ['./cyclic-treatment.component.scss'],
  standalone: false
})
export class CyclicTreatmentComponent implements OnInit {

  private static readonly APPLICATION = 'insurance';
  private static readonly RESOURCE = 'preAuthorization_requests';

  /**
   * The row this was opened on.
   *
   * The two launch paths deliver it differently and the difference is not cosmetic.
   * `DialogService.open` puts the context on `dialogConfig.data`. A capability selector goes
   * through `DynamicPopUpComponent`, which puts the grid row on `dialogConfig.item` and then
   * assigns `instance.data` from it in **ngAfterViewInit** — after this component's `ngOnInit`
   * has already run. Reading `dialogConfig.data` first therefore picked up whatever unrelated
   * payload was sitting there, and either loaded the wrong pre-authorization or none at all.
   *
   * So nothing is adopted unless it actually carries a record id, and the load is driven by the
   * setter rather than by `ngOnInit` alone.
   */
  private _data: any = {};
  private loadedId = '';

  set data(value: any) {
    const id = CyclicTreatmentComponent.idOf(value);
    if (!id) {
      // Not a record - an early or unrelated assignment. Ignore it rather than letting it
      // overwrite a row already in hand.
      return;
    }
    this._data = value;
    this.load();
  }

  get data(): any {
    return this._data;
  }

  /** A record id, or empty when this object is not one. */
  private static idOf(value: any): string {
    if (!value || typeof value !== 'object') {
      return '';
    }
    return value.id || value._id || '';
  }

  /** The pre-authorization as stored, re-read on open so the ledger is never a stale grid row. */
  preAuth: any = null;

  /** Only the cyclic procedures — a case may hold ordinary ones alongside them. */
  cyclicItems: any[] = [];

  loading = false;
  errorMessage = '';

  constructor(
    private dialogRef: DialogRef,
    private dialogConfig: DialogConfig,
    private baseService: BaseService,
    private loader: LoaderService,
    private alerts: AlertsService
  ) { }

  ngOnInit(): void {
    // `item` first: that is where DynamicPopUpComponent puts the grid row. Take the first
    // candidate that actually carries a record id and stop - assigning them in turn would let a
    // lower-priority one replace the row already in hand and fire a second load.
    const config: any = this.dialogConfig || {};
    for (const candidate of [config['item'], config.data, config]) {
      if (CyclicTreatmentComponent.idOf(candidate)) {
        this.data = candidate;
        break;
      }
    }

    if (!this.loadedId) {
      // The capability path assigns the row in ngAfterViewInit, after this runs, so an empty
      // handoff here is normal and must not be reported as a failure yet. Give that assignment a
      // turn of the event loop before deciding nothing is coming.
      setTimeout(() => {
        if (!this.loadedId) {
          this.errorMessage =
            'This pre-authorization could not be identified, so its cycles cannot be shown.';
        }
      });
    }
  }

  // ---------------------------------------------------------------- loading

  private get preAuthId(): string {
    return this.data.id || this.data._id || '';
  }

  load(force?: boolean): void {
    if (!this.preAuthId) {
      return;
    }
    if (!force && this.loadedId === this.preAuthId && this.loading) {
      return;
    }
    // A real load is starting, so clear anything said while the row was still being handed over.
    this.errorMessage = '';
    this.loadedId = this.preAuthId;
    this.loading = true;
    this.errorMessage = '';
    this.baseService
      .getById(this.preAuthId, CyclicTreatmentComponent.RESOURCE, CyclicTreatmentComponent.APPLICATION)
      .subscribe(
        (res: any) => {
          this.loading = false;
          this.preAuth = this.firstResource(res);
          this.cyclicItems = this.collectCyclicItems(this.preAuth);
          if (!this.cyclicItems.length) {
            // Say which of the two it is. The worklist only offers this action on a case whose
            // items carry cyclicProcedure = Y, so landing here means either the record came back
            // without its procedures or its procedures disagree with the row - and those call for
            // very different investigation.
            const items = (this.preAuth && this.preAuth.item_details) || [];
            this.errorMessage = items.length
              ? 'None of the ' + items.length + ' procedures on this pre-authorization is marked as cyclic.'
              : 'This pre-authorization came back without any procedures, so its cycles cannot be shown.';
          }
        },
        (err: any) => {
          this.loading = false;
          this.errorMessage = this.messageOf(err, 'The pre-authorization could not be loaded.');
        }
      );
  }

  private collectCyclicItems(preAuth: any): any[] {
    const items = (preAuth && preAuth.item_details) || [];
    return items.filter((item: any) => String(item.cyclicProcedure || '').toUpperCase() === 'Y');
  }

  // ---------------------------------------------------------------- ledger shape

  /**
   * One row per approved cycle, recorded or not, so the operator sees the whole plan rather than
   * only what has happened. Rows past the next outstanding one are shown locked: cycles are
   * delivered in order, and offering "capture cycle 4" while cycle 3 is outstanding invites a
   * mis-numbered record the payer would reject.
   */
  rowsFor(item: any): any[] {
    const recorded = this.recordedFor(item);
    const approved = this.approvedFor(item) || recorded.length;
    const rows = [];
    for (let i = 0; i < approved; i++) {
      const cycle = recorded[i];
      rows.push({
        cycleNumber: i + 1,
        cycle: cycle || null,
        captured: !!cycle,
        next: !cycle && i === recorded.length,
        locked: !cycle && i > recorded.length
      });
    }
    return rows;
  }

  recordedFor(item: any): any[] {
    const cycles = (item && item.cycle_details) || [];
    // Stored order is append order, but sort defensively so a hand-edited record still reads right.
    return cycles.slice().sort((a: any, b: any) => (a.cycleNumber || 0) - (b.cycleNumber || 0));
  }

  /**
   * Cycles this procedure may actually deliver.
   *
   * The provider requests `noOfCycles`; the payer may approve fewer, and because a cyclic item is
   * sent with its cycle count as `item.quantity`, the payer's approved quantity comes back as the
   * approved cycle count. Once it exists it is the ceiling — showing four slots against a
   * three-cycle approval would invite a fourth capture the claim then has to refuse.
   */
  approvedFor(item: any): number {
    return Number(item.approvedQuantity) || Number(item.noOfCycles) || 0;
  }

  /** True when the payer cut the count back, so the ledger can say so rather than just shrinking. */
  wasReduced(item: any): boolean {
    const approved = Number(item.approvedQuantity) || 0;
    const requested = Number(item.noOfCycles) || 0;
    return approved > 0 && requested > 0 && approved < requested;
  }

  /**
   * The cycle's treatment period as one readable span, or empty when it has none.
   *
   * The period is what the claim sends as the cycle's `timingPeriod`, so the operator needs to see
   * back what they entered — the treatment date alone does not show how long the session ran.
   */
  periodOf(cycle: any): string {
    const period = cycle && cycle.service_validity;
    if (!period || !period.startDate) {
      return '';
    }
    const start = this.formatDateTime(period.startDate);
    const end = period.endDate ? this.formatDateTime(period.endDate) : '';
    return end && end !== start ? start + ' \u2013 ' + end : start;
  }

  private formatDateTime(millis: number): string {
    const date = new Date(Number(millis));
    if (isNaN(date.getTime())) {
      return '';
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return pad(date.getDate()) + '/' + pad(date.getMonth() + 1) + ' '
      + pad(date.getHours()) + ':' + pad(date.getMinutes());
  }

  procedureLabel(item: any): string {
    return item.serviceItemName || item.service_code || '';
  }

  /**
   * Authentications the case carries so far: the pre-authorization's own plus one per recorded
   * cycle. The claim's own adds the last one — four cycles reaching six in total.
   *
   * The pre-authorization counts for one only when it was actually authenticated; a case admitted
   * on the Authentication Consent Questionnaire has none, and claiming otherwise would overstate
   * the trail on the one screen an operator checks it against.
   */
  get authenticationCount(): number {
    let cycles = 0;
    this.cyclicItems.forEach((item: any) => { cycles += this.recordedFor(item).length; });
    const preAuthOwn = (this.preAuth && this.preAuth.biometric_auth_txn_id) ? 1 : 0;
    return preAuthOwn + cycles;
  }

  // ---------------------------------------------------------------- capture

  /**
   * True when a cycle for this procedure was already authenticated today.
   *
   * PAYR-1369 rejects a case carrying two biometric authentications on the same date for the same
   * cyclic procedure, so a second capture today is refused here rather than being recorded and
   * bouncing back from the payer weeks later.
   */
  capturedToday(item: any): boolean {
    const midnight = this.startOfDay(Date.now());
    return this.recordedFor(item).some((cycle: any) => this.startOfDay(cycle.treatmentDate) === midnight);
  }

  canCapture(item: any): boolean {
    const recorded = this.recordedFor(item).length;
    const approved = this.approvedFor(item);
    return !this.loading && recorded < approved && !this.capturedToday(item);
  }

  /** Why the capture button is disabled, so a greyed button is never unexplained. */
  blockedReason(item: any): string {
    const recorded = this.recordedFor(item).length;
    const approved = this.approvedFor(item);
    if (approved && recorded >= approved) {
      return 'All ' + approved + ' approved cycles have been recorded.';
    }
    if (this.capturedToday(item)) {
      return 'A cycle was already authenticated today. A beneficiary cannot be authenticated more than once on the same date.';
    }
    return '';
  }

  /**
   * Opens the biometric dialog for the next outstanding cycle, then records what comes back.
   *
   * The mode is the operator's own choice, handed straight to the dialog — face, fingerprint and
   * iris all complete through the same flow, so nothing here needs to know which.
   */
  capture(item: any, authMode: string): void {
    if (!this.canCapture(item)) {
      return;
    }
    const dialogService = this.baseService?.modalService?.dialogService;
    if (!dialogService) {
      return;
    }
    const cycleNumber = this.recordedFor(item).length + 1;

    dialogService
      .open(BiometricAuthComponent, {
        data: {
          linkType: 'PREAUTH',
          authMode: authMode,
          authentication_type_code: String(authMode || '').toLowerCase(),
          // Fingerprint and iris identify the beneficiary to ABDM up front by ABHA number; face
          // identifies them at verify time by Aadhaar, so an empty value is fine there.
          abhaNumber: this.preAuth.abha_number || this.preAuth.abhaNumber || '',
          payerCode: this.preAuth.payerCode || '',
          payerName: this.preAuth.payerName || '',
          payerId: this.preAuth.payerId || '',
          sender_code: this.preAuth.sender_code || '',
          receipt_code: this.preAuth.receipt_code || '',
          receipt_name: this.preAuth.receipt_name || '',
          productId: this.preAuth.productId || '',
          uhId: this.preAuth.uhId || '',
          encounter_id: this.preAuth.encounter_id || '',
          patient_name: this.preAuth.patientFirstName || this.preAuth.patient_name || '',
          mobile: this.preAuth.mobileNumber || '',
          preAuthId: this.preAuth.id || '',
          preAuthReferenceNumber: this.preAuth.preAuthReferenceNumber || '',
          // Carried so the authentication row itself records which cycle of which procedure it
          // covers - the trail the payer's PAYR-1367 lookup is judged against.
          procedureRef: item.service_code || '',
          cycleNumber: cycleNumber
        },
        title: 'field.label.verify_biometric_auth',
        size: DialogModalSize.Md
      })
      .afterClosed.subscribe((result: any) => {
        if (!result || !result.txnId) {
          // Cancelled or abandoned: nothing is recorded, the cycle stays outstanding.
          return;
        }
        this.recordCycle(item, result.txnId, result.cyclic_doc, result.cyclic_doc_title, result.service_validity);
      }, () => { });
  }

  /**
   * Posts the verified authentication as a delivered cycle.
   *
   * Only the procedure and the txnId are sent. The cycle number, its treatment date and every
   * limit are decided by the backend from the stored pre-authorization and the stored
   * authentication — a client that sent its own could pre-date a cycle that never happened.
   */
  private recordCycle(item: any, txnId: string, cyclicDoc?: string, cyclicDocTitle?: string,
                      servicePeriod?: any): void {
    const cycle: any = { biometricAuthTxnId: txnId, cyclic_doc: cyclicDoc || '', cyclic_doc_title: cyclicDocTitle || '' };
    // Only sent when the operator picked one - an empty period would otherwise be stored as a
    // pair of zeroes and read back as a real, if nonsensical, service window.
    if (servicePeriod) {
      cycle.service_validity = servicePeriod;
    }
    const body: any = {
      id: this.preAuth.id,
      item_details: [
        {
          service_code: item.service_code,
          cycle_details: [cycle]
        }
      ]
    };
    this.loader.showSpinner();
    this.baseService
      .action(body, 'RECORD_CYCLE_BIOMETRIC', CyclicTreatmentComponent.APPLICATION,
        CyclicTreatmentComponent.RESOURCE, false)
      .subscribe(
        () => {
          this.loader.hideSpinner();
          this.alerts.success('Cycle recorded for ' + this.procedureLabel(item) + '.');
          // Re-read rather than patching in place: the backend owns the numbering and the
          // delivered count, so the stored record is the only version worth showing.
          this.load(true);
        },
        (err: any) => {
          this.loader.hideSpinner();
          this.errorMessage = this.messageOf(err, 'The cycle could not be recorded.');
        }
      );
  }

  close(): void {
    this.dialogRef.close();
  }

  // ---------------------------------------------------------------- helpers

  private startOfDay(millis: number): number {
    if (!millis) {
      return 0;
    }
    const date = new Date(millis);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  }

  private firstResource(res: any): any {
    if (!res) {
      return null;
    }
    const list = res.resource || res.resources || res;
    return Array.isArray(list) ? (list.length ? list[0] : null) : list;
  }

  private messageOf(err: any, fallback: string): string {
    if (!err) {
      return fallback;
    }
    if (typeof err === 'string') {
      return err;
    }
    return err.error?.message || err.message || fallback;
  }
}
